import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import api from "@/utils/api";
import { clearCachedInferenceResults } from "@/utils/api/datasets";
import { useAuth } from "@/contexts/AuthContext";
import {
    useCurrentWorkflow,
    useCurrentWorkspace,
    useFetchWorkflowByUrl,
    useTokenError,
    useWorkflowError,
    useWorkflowToken,
    useClearMediaState,
    useSetCurrentMedia
} from "@/store";
import LoadingScreen from "@/components/LoadingScreen";
import { RapidWorkflowDataType } from "@/components/flow/shared/Deploy/documentation/types";
import { ErrorInfo } from "@/components/ErrorInfo";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { useMedia } from "@/contexts/MediaContext";

export const HydrateWorkflow = ({ children }: { children: React.ReactNode }) => {
    const { workflowUrl } = useParams<{ workflowUrl: string }>();
    const { authenticatedWorkspace } = useAuth();
    const fetchWorkflowByUrl = useFetchWorkflowByUrl();
    const navigate = useNavigate();

    const currentWorkspace = useCurrentWorkspace();
    const currentWorkflow = useCurrentWorkflow();
    const workflowError = useWorkflowError();
    const workflowToken = useWorkflowToken();
    const tokenError = useTokenError();
    const clearMediaState = useClearMediaState();
    const setCurrentMedia = useSetCurrentMedia();
    const { setMediaItemIds, mediaItems } = useMedia();

    // Clear media when workflow URL doesn't match current workflow
    // This handles both:
    // 1. Component remounting due to route key change (fresh component with stale media)
    // 2. Same component detecting URL change
    useEffect(() => {
        // If we have media but the current workflow doesn't match the URL, clear it
        // This happens when navigating between workflows because the route key causes remount
        // but the MediaProvider persists and keeps old media
        if (mediaItems.length > 0 && currentWorkflow?.url !== workflowUrl) {
            console.log("[HydrateWorkflow] ========== CLEARING MEDIA STATE ==========");
            console.log("[HydrateWorkflow] currentWorkflow.url:", currentWorkflow?.url);
            console.log("[HydrateWorkflow] workflowUrl:", workflowUrl);
            console.log("[HydrateWorkflow] mediaItems.length:", mediaItems.length);
            // Clear both the store's media object AND the context's mediaItemIds
            clearMediaState();
            setMediaItemIds([]);
            setCurrentMedia(undefined);
            // Clear predictions cache for the old workflow when switching workflows
            // (but not when navigating within the same workflow, e.g., deploy <-> improve)
            if (currentWorkflow?.url) {
                console.log(
                    "[HydrateWorkflow] Clearing predictions cache for:",
                    currentWorkflow.url
                );
                clearCachedInferenceResults(currentWorkflow.url);
            }
            console.log("[HydrateWorkflow] Media state cleared!");
        }
    }, [
        workflowUrl,
        currentWorkflow?.url,
        mediaItems.length,
        clearMediaState,
        setMediaItemIds,
        setCurrentMedia
    ]);

    const onHydrateWorkflowError = useCallback(
        (e: Error) => {
            console.log("🐞[DEBUG - hydrateWorkflow] error", e, "redirecting to home");
            alert(
                `There was an error hydrating the workflow: ${e}. You will be redirected to the home page.`
            );
            navigate("/");
        },
        [navigate]
    );

    const onHydrateWorkflowSuccess = useCallback(
        (workflow: RapidWorkflowDataType | null) => {
            if (workflow) {
                // success. wait for currentWorkflow to propagate
            } else {
                onHydrateWorkflowError(new Error("No workflow found"));
            }
        },
        [onHydrateWorkflowError]
    );

    const handleFetchWorkflowByUrl = useCallback(async () => {
        console.log("🐞[DEBUG - hydrateWorkflow] handleFetchWorkflowByUrl", {
            authenticatedWorkspace,
            workflowUrl,
            currentWorkflow,
            currentWorkflowUrl: currentWorkflow?.url,
            currentWorkspaceId: currentWorkspace?.id
        });
        if (
            authenticatedWorkspace &&
            workflowUrl &&
            (currentWorkflow?.url !== workflowUrl || !workflowToken)
        ) {
            await fetchWorkflowByUrl(
                workflowUrl,
                currentWorkspace?.id!,
                api,
                onHydrateWorkflowSuccess
            );
        }
    }, [
        authenticatedWorkspace,
        currentWorkflow,
        currentWorkspace?.id,
        fetchWorkflowByUrl,
        onHydrateWorkflowSuccess,
        workflowToken,
        workflowUrl
    ]);

    const {
        isLoading: isLoadingFetchWorkflowByUrl,
        error: errorFetchWorkflowByUrl,
        invoke: invokeFetchWorkflowByUrl
    } = useAsyncBoundOperation(handleFetchWorkflowByUrl, { autoInvoke: true });

    const error = workflowError || tokenError || errorFetchWorkflowByUrl;

    const handleErrorRetry = useCallback(() => {
        invokeFetchWorkflowByUrl();
    }, [invokeFetchWorkflowByUrl]);

    if (error) {
        return (
            <div className="flex h-full items-center justify-center p-4">
                <ErrorInfo error={error} onRetryClick={handleErrorRetry} />
            </div>
        );
    }

    // wait for workflow switch to complete
    if (!currentWorkflow || currentWorkflow.url !== workflowUrl || isLoadingFetchWorkflowByUrl) {
        return <LoadingScreen />;
    }

    return children;
};
